import { defineStore, storeToRefs } from 'pinia'
import { ref, toRefs } from 'vue'
// @ts-expect-error: vuex
import { useStore } from 'vuex'

import childEditionService from '@/services/http/api/childEdition'
import { RecommendationSchema } from '@/schemas/nextEditionRecommendations'
import { apiPermissionsFlags } from '@/helpers/nextEditionDocuments'
import { useChildEditionStore } from './childEdition'
import { isForbidden, isNotFound, isAxiosError } from '@/helpers/stores/axiosErrorCheck'
import { prepareData } from '@/helpers/stores/documents'
import { useErrorModal } from '@/composables/useErrorModal'
import { useNextEditionCoordinatorQuestionnaireStore } from '@/stores'

import resourceService from '@/services/http/api/resource'
import nextEditionChildDocument from '@/services/http/api/nextEditionChildDocument'

import type { Recommendation, ChildEditionId, NextEditionDocumentName, TODO } from '@/types'
import { useNextEditionGuardianConsent } from './nextEditionChildDocument/nextEditionGuardianConsent'

export const useNextEditionChildDocumentStore = defineStore('nextEditionChildDocument', () => {
  const { setErrorModalOnError } = useErrorModal()
  const vuexStore = useStore()
  const nextEditionCoordinatorQuestionnaireStore = useNextEditionCoordinatorQuestionnaireStore()
  const useNextEditionGuardianConsentStore = useNextEditionGuardianConsent()

  const { coordinatorQuestionnaire } = storeToRefs(nextEditionCoordinatorQuestionnaireStore)
  const { postCoordinatorFlag, postCoordinatorFirst, getCoordinatorFlag, getCoordinatorFirst } =
    apiPermissionsFlags()

  type DefaultState = {
    recommendationsLoader: boolean
    recommendationsEnabled: boolean
    recommendations: Recommendation
    loader: boolean
    nextEditionDocument: TODO
    resource: TODO
    sendLoader: boolean
    editMode: boolean
  }

  const defaultState: DefaultState = {
    recommendationsLoader: false,
    recommendationsEnabled: false,
    recommendations: {},
    loader: false,
    nextEditionDocument: null,
    resource: null,
    sendLoader: false,
    editMode: false
  }

  const state = ref({ ...defaultState })

  const disableRecommendationsOnForbidden = (error: unknown) => {
    if (isForbidden(error)) {
      state.value.recommendationsEnabled = false
      return
    }
    console.error(error)
  }

  const fetchRecommendations = async (
    childEditionId: ChildEditionId,
    isCoordinator = getCoordinatorFirst
  ) => {
    state.value.recommendationsLoader = true
    try {
      const { data } = await childEditionService?.getNextEditionRecommendations(
        childEditionId,
        isCoordinator
      )

      RecommendationSchema.parse(data?.data)
      state.value.recommendations = data?.data
      state.value.recommendationsEnabled = true
    } catch (error) {
      if (isForbidden(error) && getCoordinatorFlag && !isCoordinator) {
        try {
          await fetchRecommendations(childEditionId, true)
        } catch (error) {
          disableRecommendationsOnForbidden(error)
        }
      }
      disableRecommendationsOnForbidden(error)
    } finally {
      state.value.recommendationsLoader = false
    }
  }

  const clear = () => {
    Object.assign(state.value, defaultState)
  }

  const setResource = (payload: TODO) => {
    state.value.resource = {
      ...state.value.resource,
      ...payload
    }
  }

  type FetchResourcePayload = {
    nextEditionDocumentType: NextEditionDocumentName
    isPublic?: boolean
    editionName?: string
  }
  const fetchResource = async (payload: FetchResourcePayload) => {
    try {
      const isPublic = payload?.isPublic === true
      const { data } = await resourceService.document(
        payload.nextEditionDocumentType,
        isPublic,
        payload?.editionName
      )
      setResource(data?.data)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const nextEditionDocumentSetterMap = {
    next_edition_coordinator_recommendation:
      nextEditionCoordinatorQuestionnaireStore.setCoordinatorQuestionnaire,
    next_edition_application: useNextEditionGuardianConsentStore.setForm
  }
  const nextEditionDocumentGetterMap = {
    next_edition_coordinator_recommendation: coordinatorQuestionnaire
  }

  type FetchNextEditionDocumentPayload = {
    childEditionId: ChildEditionId
    documentType: NextEditionDocumentName
    isCoordinator?: boolean
  }
  const fetchNextEditionDocument = async (payload: FetchNextEditionDocumentPayload) => {
    const isCoordinator = payload?.isCoordinator ?? getCoordinatorFirst
    try {
      const { data } = await nextEditionChildDocument.getByType(payload, isCoordinator)
      state.value.nextEditionDocument = data?.data
      const nextEditionDocumentType: NextEditionDocumentName = payload?.documentType
      if (!(nextEditionDocumentType in nextEditionDocumentSetterMap))
        throw new Error('Invalid key in nextEditionDocumentSetterMap')
      nextEditionDocumentSetterMap[
        nextEditionDocumentType as keyof typeof nextEditionDocumentSetterMap
      ](data?.data?.content)
      return data?.data
    } catch (error) {
      if (isNotFound(error)) {
        state.value.editMode = true
        return
      }
      if (isForbidden(error) && getCoordinatorFlag && !isCoordinator) {
        fetchNextEditionDocument({ ...payload, isCoordinator: true })
      } else throw error
    }
  }

  const childEditionStore = useChildEditionStore()

  type InitPayload = Omit<FetchResourcePayload, 'editionName'> &
    Pick<FetchNextEditionDocumentPayload, 'childEditionId'>
  const init = async (payload: InitPayload) => {
    clear()
    nextEditionCoordinatorQuestionnaireStore.clearCoordinatorQuestionnaire()
    state.value.loader = true

    try {
      const editionName = await childEditionStore.getChildsEditionName(payload.childEditionId)
      const resourceResponse = fetchResource({
        editionName,
        nextEditionDocumentType: payload.nextEditionDocumentType
      })
      const nextEditionDocumentResponse = fetchNextEditionDocument({
        childEditionId: payload.childEditionId,
        documentType: payload.nextEditionDocumentType
      })
      const fetchesResponses = [await resourceResponse, await nextEditionDocumentResponse]
      state.value.recommendationsEnabled = true
      return fetchesResponses
    } catch (error) {
      console.error(error)
    } finally {
      state.value.loader = false
    }
  }

  const responseFunction = (data: TODO) => {
    const nextEditionCoordinatorStatus = data?.data?.next_edition_coordinator_recommendation?.status
    if (nextEditionCoordinatorStatus)
      state.value.recommendations.next_edition_coordinator_recommendation_status =
        nextEditionCoordinatorStatus

    const nextEditionApplicationStatus = data?.data?.next_edition_application?.status
    if (nextEditionApplicationStatus)
      state.value.recommendations.next_edition_application_status = nextEditionApplicationStatus

    const { school_hydra_users: schoolHydraUsers } =
      vuexStore.getters['childEdition/getChild'] ?? {}

    const childEdition = data?.data?.child_edition

    if (childEdition && typeof childEdition === 'object') {
      vuexStore.commit(
        'childEdition/setChild',
        {
          child_edition: childEdition,
          school_hydra_users: schoolHydraUsers
        },
        { root: true }
      )
    }
    vuexStore.commit('childEdition/setChildEditionRequestSkipped', true, { root: true })
    return data
  }

  type PostNextEditionDocumentPayload = {
    childEditionId: ChildEditionId
    documentType: NextEditionDocumentName
    isCoordinator?: boolean
  }
  const send = async (payload: PostNextEditionDocumentPayload) => {
    const isCoordinator = payload?.isCoordinator ?? postCoordinatorFirst
    state.value.sendLoader = true
    try {
      const nextEditionDocumentType: NextEditionDocumentName = payload?.documentType
      if (!(nextEditionDocumentType in nextEditionDocumentGetterMap))
        throw new Error('Invalid key in nextEditionDocumentGetterMap')
      const documentData =
        nextEditionDocumentGetterMap[
          nextEditionDocumentType as keyof typeof nextEditionDocumentGetterMap
        ]
      const payloadData = prepareData({
        ...payload,
        data: documentData.value
      })
      const { data } = await nextEditionChildDocument.post(payloadData, isCoordinator)
      responseFunction(data)
    } catch (error) {
      if (isForbidden(error) && postCoordinatorFlag && !isCoordinator) {
        await send({ ...payload, isCoordinator: true })
        return
      }
      if (isAxiosError(error)) {
        setErrorModalOnError(error)
        return
      }
      throw error
    } finally {
      state.value.sendLoader = false
    }
  }

  return {
    ...toRefs(state.value),
    fetchRecommendations,
    init,
    send
  }
})
